/**
 * React hook for reading a record's closure trail.
 *
 * Wraps `closureService.getTrail` with state management
 * (`{ data, isLoading, error, refetch }`) — the harmony-ap `useCompliance`
 * shape. Resolves the `Closure Links` + target-register list ids from
 * `useGraphClient().listIds` (never hard-coded) and passes them to the service.
 * 04-08 (closure-trail surface) consumes this.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useGraphClient } from './useGraphClient';
import { getTrail } from '../services/closureService';
import type { ClosureListIds } from '../services/closureService';
import type { ClosureTrail } from '../types/qms';
import type { ListNameKey } from '../config/sharepoint';

export interface UseClosureTrailResult {
  data: ClosureTrail | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Read the closure trail for a source record reference (e.g. `INC-0042`).
 * `data` is null until loaded; an unprovisioned `Closure Links` list yields an
 * empty trail rather than an error.
 */
export function useClosureTrail(sourceRef: string | null): UseClosureTrailResult {
  const { client, siteId, listIds, isReady } = useGraphClient();
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const refetch = useCallback(() => {
    setFetchTrigger((n) => n + 1);
  }, []);

  // One object per load; loading is derived, not set inside the effect (13-08, ISS-185).
  const request = useMemo(
    () =>
      isReady && client && siteId && listIds && sourceRef
        ? { client, siteId, listIds, sourceRef, fetchTrigger }
        : null,
    [client, siteId, listIds, isReady, sourceRef, fetchTrigger],
  );
  const [result, setResult] = useState<{
    request: typeof request;
    data: ClosureTrail | null;
    error: string | null;
  }>({ request: null, data: null, error: null });

  useEffect(() => {
    if (!request) return;

    const ids = request.listIds;
    const closureListIds: ClosureListIds = {
      closureLinks: ids['closureLinks' as ListNameKey] ?? '',
      continuousImprovement: ids['continuousImprovement' as ListNameKey] ?? '',
      risk: ids['risk' as ListNameKey] ?? '',
      carePlanRevisions: ids['carePlanRevisions' as ListNameKey] ?? '',
    };

    let cancelled = false;

    getTrail(request.client, request.siteId, closureListIds, request.sourceRef)
      .then((trail) => {
        if (!cancelled) setResult({ request, data: trail, error: null });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load closure trail';
          // As before: a failed load keeps the previous trail and reports the error.
          setResult((prev) => ({ request, data: prev.data, error: message }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [request]);

  const isLoading = request !== null && result.request !== request;
  return { data: result.data, isLoading, error: isLoading ? null : result.error, refetch };
}
